/**
 * The administrator group: the one rule about its name, and the name to offer.
 *
 * gdsgate keeps the `gdsgate-` namespace for the identities a cluster issues
 * itself — `gdsgate-admin`, `gdsgate-provisioner`, `gdsgate-break-glass`. A
 * tenancy's Auth refuses an identity provider's claim inside it, the subject or
 * any one group, without regard to case; and it refuses **the whole token**
 * rather than dropping the one claim. Somebody carrying such a group does not
 * sign in at all.
 *
 * So a tenancy declared with such a group is a cluster nobody ever signs in to,
 * and the field cannot be corrected afterwards: it is substituted into the Cedar
 * policy the cluster is seeded with, the cluster's own store is authoritative
 * from its first start, and the control plane cannot rewrite it. The repair is
 * declaring the tenancy again. The control plane therefore refuses the name at
 * the edge — `400 invalid_request` with `fields.admin_group` — and this console
 * refuses it a moment earlier, while somebody is still typing, and above all
 * stops *suggesting* one.
 *
 * The prescribed shape is `<slug>-admins`, and for exactly one slug it walks
 * into the namespace by itself: `gdsgate`. That is a warning on the form rather
 * than a surprise on submission, because the field is the one that cannot be
 * changed later.
 *
 * The contract is `gdsgate-saas`, docs/api.md §6 (`POST /api/account/tenants`)
 * and §6a (why the field never moves).
 */

import type { ApiError } from '../../api/errors';
import type { MessageParams, SimpleKey } from '../../i18n/types';

/** The namespace gdsgate keeps for the identities a cluster issues itself. */
export const CLUSTER_IDENTITY_NAMESPACE = 'gdsgate-';

/**
 * Whether a group name falls inside that namespace.
 *
 * A prefix rule, and case is not looked at, because that is how the cluster
 * compares it: `GDSGATE-admins` is the same name, and `acme-gdsgate-admins` is
 * an ordinary one.
 */
export function isReservedGroup(name: string): boolean {
  return name.trim().toLowerCase().startsWith(CLUSTER_IDENTITY_NAMESPACE);
}

/** The shape the contract prescribes for a tenancy's administrators. */
export function prescribedAdminGroup(slug: string): string {
  return `${slug.trim().toLowerCase()}-admins`;
}

/**
 * The name to put in the box for the slug that has been typed, or `null` when
 * there is none to offer: no slug yet, or a slug whose prescribed name lands in
 * the namespace. Offering nothing is the point — a suggestion the server
 * refuses is worse than no suggestion at all.
 */
export function suggestedAdminGroup(slug: string): string | null {
  if (slug.trim() === '') return null;
  const prescribed = prescribedAdminGroup(slug);
  return isReservedGroup(prescribed) ? null : prescribed;
}

/** Whether this slug takes the prescribed name away instead of providing one. */
export function slugTakesThePrescribedName(slug: string): boolean {
  return slug.trim() !== '' && suggestedAdminGroup(slug) === null;
}

/** A sentence to put under the box: which one, and what to fill into it. */
export interface GroupComplaint {
  key: SimpleKey;
  params: MessageParams;
}

/**
 * The console's own words for a name inside the namespace, in whichever
 * language is being read.
 *
 * It names a replacement where the slug provides one, and where the slug is the
 * one that does not, it says to pick a name outside the namespace and stops
 * there rather than inventing one.
 */
export function reservedComplaint(slug: string): GroupComplaint {
  const suggestion = suggestedAdminGroup(slug);
  if (suggestion === null) {
    return {
      key: 'createTenant.check.adminGroupReserved',
      params: { namespace: CLUSTER_IDENTITY_NAMESPACE },
    };
  }
  return {
    key: 'createTenant.check.adminGroupReservedTry',
    params: { namespace: CLUSTER_IDENTITY_NAMESPACE, name: suggestion },
  };
}

/**
 * What belongs under the group box when the server refused the declaration.
 *
 * `null` when the refusal did not name that box. Otherwise one of two, and the
 * branch is taken on **what was sent**, never on the server's prose: a name
 * this console can see is inside the namespace it says so itself, in the
 * reader's language and with the replacement the slug implies; anything else
 * carries the server's own sentence, which is then the only thing describing
 * what happened.
 *
 * Called where the refusal is already known to be `invalid_request` — the field
 * complaint is the signal, as it is for every other box on this form.
 */
export type AdminGroupRefusal =
  | { known: true; complaint: GroupComplaint }
  | { known: false; reason: string };

export function adminGroupRefusal(
  failure: ApiError,
  sent: { slug: string; admin_group: string },
): AdminGroupRefusal | null {
  const reason = failure.fieldError('admin_group');
  if (reason === undefined) return null;
  if (isReservedGroup(sent.admin_group)) {
    return { known: true, complaint: reservedComplaint(sent.slug) };
  }
  return { known: false, reason };
}
